import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { chartColors, formatCurrency } from "./chartUtils";

interface AreaChartData {
  name: string;
  [key: string]: string | number;
}

interface AreaChartProps {
  data: AreaChartData[];
  dataKeys: { key: string; color?: string; name?: string; gradientId?: string }[];
  title?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  height?: number;
  stacked?: boolean;
}

export function AreaChart({
  data,
  dataKeys,
  title,
  showLegend = true,
  showGrid = true,
  height = 300,
  stacked = false,
}: AreaChartProps) {
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: { value: number; name: string; color: string }[];
    label?: string;
  }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone/10">
          <p className="font-medium text-charcoal mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full" style={{ height }}>
      {title && (
        <h3 className="text-sm font-medium text-charcoal mb-2">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <RechartsAreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            {dataKeys.map((dk, index) => {
              const color = dk.color || chartColors.primary[index % chartColors.primary.length];
              return (
                <linearGradient
                  key={`gradient-${dk.key}`}
                  id={dk.gradientId || `gradient-${dk.key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              );
            })}
          </defs>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DC" />}
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: "#8B8589" }}
            axisLine={{ stroke: "#E8E3DC" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#8B8589" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => formatCurrency(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value: string) => (
                <span className="text-sm text-charcoal">{value}</span>
              )}
            />
          )}
          {dataKeys.map((dk, index) => {
            const color = dk.color || chartColors.primary[index % chartColors.primary.length];
            return (
              <Area
                key={dk.key}
                type="monotone"
                dataKey={dk.key}
                name={dk.name || dk.key}
                stroke={color}
                strokeWidth={2}
                fill={`url(#${dk.gradientId || `gradient-${dk.key}`})`}
                stackId={stacked ? "stack" : undefined}
              />
            );
          })}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </div>
  );
}
